import { Button, CircularProgress, Typography } from '@mui/material';
import { Play } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticatedObjectUrl } from '../hooks/useAuthenticatedObjectUrl';
import type { VideoArtifact } from '../types';

const ASPECT_CLASSES: Record<string, string> = {
  '16:9': 'aspect-video',
  '9:16': 'aspect-[9/16] max-h-[480px] mx-auto',
  '1:1': 'aspect-square max-h-[480px] mx-auto',
};

interface VideoJobPlayerProps {
  artifact: VideoArtifact;
  aspectRatio: string | null;
}

/** Loads the (authenticated) video only when the user asks to play it. */
export const VideoJobPlayer: React.FC<VideoJobPlayerProps> = ({ artifact, aspectRatio }) => {
  const { t } = useTranslation();
  const [requested, setRequested] = useState(false);
  const { url, failed, loading } = useAuthenticatedObjectUrl(
    requested ? artifact.previewUrl : null
  );
  const frame = `${ASPECT_CLASSES[aspectRatio ?? ''] ?? 'aspect-video'} w-full overflow-hidden rounded-lg bg-slate-900`;

  if (url) {
    return (
      <video className={frame} src={url} controls autoPlay playsInline preload="metadata">
        <track kind="captions" />
      </video>
    );
  }

  return (
    <div className={`${frame} flex flex-col items-center justify-center gap-2`}>
      {loading ? (
        <CircularProgress size={28} className="text-white" />
      ) : (
        <Button
          variant="contained"
          startIcon={<Play size={18} />}
          onClick={() => setRequested(true)}
          aria-label={t('services.generateVideo.play')}
        >
          {t('services.generateVideo.play')}
        </Button>
      )}
      {failed && (
        <Typography variant="caption" className="text-red-300">
          {t('services.generateVideo.previewFailed')}
        </Typography>
      )}
    </div>
  );
};
